import * as React from "react"
import { List, Map, OrderedMap, Set } from "immutable"
import { Loader } from "./loader"
import { Error } from "./error"
import { Period, PeriodSlug } from "../components/region_and_date_filter"
import { ApiResult, getPage, Region, Event } from "../../custom_api/api"
import { ActivityClustersPageProps, ActivityClustersPage, RegionInfo } from "../pages/activity_clusters_page"
import { ActivityDetailPageProps, ActivityDetailPage } from "../pages/activity_detail_page"
import { GeneralContentPageProps, GeneralContentPage } from "../pages/general_content_page"
import { CollectionPageProps, CollectionPage } from "../pages/collection_page"
import { HomePageProps, HomePage } from "../pages/home_page"
import { LocationDetailPageDrupalProps, LocationDetailPage } from "../pages/location_detail_page"
import { ReportOverviewPageProps, ReportOverviewPage } from "../pages/report_overview_page"
import { ActivityClusterProps } from "./activity_cluster"
import { Activity } from "../../custom_models/drupal"
import { isSmallViewport } from "../util"
import * as DrupalModel from '../../custom_models/drupal'

export type Page =
  | ActivityDetailPageProps
  | ActivityClustersPageProps
  | CollectionPageProps
  | GeneralContentPageProps
  | LocationDetailPageDrupalProps
  | ReportOverviewPageProps
  | HomePageProps

interface PageRendererProps {
  readonly route: string
  readonly params: string
  readonly regions: ApiResult<Map<string, Region>>
  readonly selectedPeriod: PeriodSlug
  readonly selectedRegion: string
  readonly selectedCluster: 'none' | string
  readonly selectPeriod: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly selectRegion: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly resetPeriod: () => void
  readonly toggleArchive: (cb?: () => void) => void
  readonly showArchive: boolean
  readonly selectedLocationId: string
  readonly branchLocations: DrupalModel.Location[]
  readonly selectLocation: (selectedId: string) => void
}

export type DateRangeOption = 'any' | 'vandaag' | 'morgen' | 'komende-zeven-dagen' | 'komende-dertig-dagen'
export type DateRanges = { [k in DateRangeOption]: DateRange }
export interface DateRange { from: Date, until: Date }
export type DateRangeEventCount = {
  today: number
  tomorrow: number
  seven_days: number
  thirty_days: number
  any: number
}

export interface ActivityClustersPageState extends ActivityClustersFilterState {
  readonly clusters: ApiResult<List<ActivityClusterProps>>
  readonly events: List<Event>
  readonly activities: Map<number, Activity>
  readonly resultCount: number
  readonly categories: OrderedMap<number, string>
  readonly regions: OrderedMap<number, RegionInfo>
  readonly pageSize: number
  readonly expandFilter: boolean
  readonly isSmallViewport: boolean
  readonly categoryEventCount: Map<string, number>
  readonly regionEventCount: Map<string, number>
  readonly anyRegionEventCount: number
  readonly datesEventCount: DateRangeEventCount
}

export interface ActivityClustersFilterState {
  readonly selectedRegion?: number
  readonly selectedCategories: Set<number>
  readonly selectedPage: number
  readonly dateRangeOption: DateRangeOption
  readonly from: Date
  readonly until: Date
  readonly search: string
}

export const filterZeroState: ActivityClustersFilterState = {
  selectedRegion: undefined,
  selectedCategories: Set<number>(),
  selectedPage: 1,
  dateRangeOption: 'any',
  from: new Date(),
  until: new Date(9999, 11),
  search: ''
}

export const activityClustersPageStateZero: ActivityClustersPageState = {
  clusters: "loading",
  events: List<Event>(),
  activities: Map<number, Activity>(),
  resultCount: 0,
  categories: OrderedMap<number, string>(),
  regions: OrderedMap<number, RegionInfo>(),
  pageSize: 6,
  expandFilter: false,
  isSmallViewport: isSmallViewport(),
  categoryEventCount: Map<string, number>(),
  regionEventCount: Map<string, number>(),
  anyRegionEventCount: 0,
  datesEventCount: { any: 0, today: 0, tomorrow: 0, seven_days: 0, thirty_days: 0 },
  ...filterZeroState
}

interface PageRendererState {
  readonly page: ApiResult<Page>
  readonly activityPageState: ActivityClustersPageState
}

const pageRendererStateZero: PageRendererState = {
  page: "loading",
  activityPageState: activityClustersPageStateZero
}

export class PageRenderer<A> extends React.Component<PageRendererProps, PageRendererState> {
  constructor(props: PageRendererProps) {
    super(props)

    this.state = pageRendererStateZero
  }

  componentDidUpdate(prevProps: PageRendererProps, prevState: PageRendererState) {
    if (prevProps.route != this.props.route) {
      this.loadRoute()
    }
  }

  componentDidMount() {
    this.loadRoute()
  }

  loadRoute() {
    this.setState(
      s => ({ ...s, page: "loading" }),
      () => {
        getPage(this.props.route, this.props.params).then(x => this.setState(s => ({ ...s, page: x })))
        window.scrollTo(0, 0)
      }
    )
  }

  loadPageLanguage() {
    if (this.state.page != "loading" && this.state.page != "error" && this.state.page.pageLanguage != undefined && this.state.page.pageLanguage != null) {
      document.documentElement.setAttribute('lang', this.state.page.pageLanguage)
    }
  }

  updateFilterState = (newState: Partial<ActivityClustersPageState>, cb?: () => void) => {
    this.setState((prevState) => ({...prevState, activityPageState: {...prevState.activityPageState, ...newState}}), () => { if (cb) cb()})
  }

  render() {
    const p = this.props
    const s = this.state
    const pg = s.page
    this.loadPageLanguage()

    const page: JSX.Element =
      pg == "loading" ? (
        <Loader />
      ) : pg == "error" || pg == null ? (
        <Error />
      ) : pg.kind == "activityDetail" ? (
        <ActivityDetailPage
          {...pg}
          regions={p.regions}
          selectedPeriod={p.selectedPeriod}
          selectedRegion={p.selectedRegion}
          selectPeriod={p.selectPeriod}
          selectRegion={p.selectRegion}
        />
      ) : pg.kind == "activityClusters" ? (
        <ActivityClustersPage
          {...pg}
          regions={p.regions}
          selectedPeriod={p.selectedPeriod}
          selectedRegion={p.selectedRegion}
          selectedCluster={p.selectedCluster}
          selectPeriod={p.selectPeriod}
          selectRegion={p.selectRegion}
          toggleArchive={p.toggleArchive}
          showArchive={p.showArchive}
          activityPageState={s.activityPageState}
          setActivityState={(newState, cb) => this.updateFilterState(newState, cb)}
        />
      ) : pg.kind == "generalContent" ? (
        <GeneralContentPage {...pg} />
      ) : pg.kind == "collection" ? (
        <CollectionPage {...pg} />
      ) : pg.kind == "locationDetail" ? (
        <LocationDetailPage 
          {...pg}
          regions={p.regions}
          resetPeriod={p.resetPeriod}
          selectedLocationId={p.selectedLocationId}
          branchLocations={p.branchLocations}
          selectLocation={p.selectLocation}
        />
      ) : pg.kind == "reportOverview" ? (
        <ReportOverviewPage {...pg} />
      ) : pg.kind == "home" ? (
        <HomePage {...pg }
          selectedLocationId={p.selectedLocationId}
          branchLocations={p.branchLocations}
          selectLocation={p.selectLocation}
        />
      ) : <></>

    return page
  }
}
